import { useI18n } from "../../src/ui/hooks/useI18n";
import { useArianeApi } from "../../src/ui/hooks/useArianeApi";
import { forwardRef, useState } from "react";
import { useCountryConfig } from "../../src/ui/hooks/useCountryConfig";
import ReactInputMask from "react-input-mask-format";
import { SecurityNumberInputProps } from "../../src/appConfig";
import { Dayjs } from "dayjs";
import { dayjs } from "../../src/utils/dayjs";
import { LightStyledInput } from "../../src/ui/components/mui/LightStyledInput";
import { HealthRecordGenderEnum } from "@syadem/kairos-citizen-js";

export function SecurityNumberInput(props: SecurityNumberInputProps) {
  const {
    isReadonly,
    openDatepicker,
    gender,
    setGender,
    birthDate,
    setBirthDate,
    setBirthCityCode,
    defaultValue
  } = props;

  const { t } = useI18n();
  const arianeApi = useArianeApi();
  const { securityNumber } = useCountryConfig();
  const [invalidNumber, setInvalidNumber] = useState<boolean>(false);

  const extractData = (value: string) => {
    const cleanedValue = value.trim().replaceAll(" ", "").replaceAll("_", "");
    const isValid = cleanedValue && securityNumber.validator(cleanedValue);

    if (isValid && arianeApi?.cities) {
      const genderFromExternalId = retrieveGenderFromExternalId(cleanedValue.slice(0, 1));
      const birthDateFromExternalId = retrieveBirthDateFromExternalId(cleanedValue.slice(1, 3), cleanedValue.slice(3, 5));

      if (genderFromExternalId !== undefined && !gender) {
        setGender(genderFromExternalId);
      }

      if (!birthDate) {
        setBirthDate(birthDateFromExternalId);
        openDatepicker();
      }

      arianeApi?.cities.getByCode(cleanedValue.slice(5, 10)).then((data) => {
        if (data?.code) {
          setBirthCityCode(data.code);
        }
      });
    }
  }

  return (
    <>
      <LightStyledInput
        fullWidth
        label={t("common.user_infos.social_security_number")}
        placeholder="ex : 1 83 04 33 440 039 91"
        inputComponent={!isReadonly ? (SocialSecurityMaskInput as any) : undefined}
        readOnly={isReadonly}
        name="externalId"
        onChange={(e) => {
          setInvalidNumber(false);
          extractData(e.target.value);
        }}
        defaultValue={defaultValue}
        onBlur={(e) => setInvalidNumber(!securityNumber.validator((e.target.value)))}
        error={invalidNumber}
        errorMessage={invalidNumber ? t("common.errors.securityNumber") : undefined}
      />
    </>
  );
}

function retrieveGenderFromExternalId(genderDigit: string): HealthRecordGenderEnum | undefined {
  switch (genderDigit) {
    case "1":
      return "M";
    case "2":
      return "F";
    default:
      return undefined;
  }
}

function retrieveBirthDateFromExternalId(yearDigit: string, monthDigit: string): Dayjs {
  const yearFromExternalId = new Date().getFullYear().toString().slice(0, 2) + yearDigit;
  const birthDateFromExternalId = new Date(`${yearFromExternalId}-${monthDigit}-01`);

  if (birthDateFromExternalId > new Date()) {
    birthDateFromExternalId.setFullYear(parseInt(yearFromExternalId) - 100);
  }

  return dayjs.utc(birthDateFromExternalId);
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  value: string;
}

const SocialSecurityMaskInput = forwardRef<HTMLInputElement, CustomProps>(function TextMaskCustom(props, ref) {
  return (
    <ReactInputMask
      {...props}
      mask="9 99 99 9* 999 999 99"
      onChange={props.onChange}
      value={props.value}
      ref={ref as any}
    />
  );
});
